import { inject, Injectable, Injector, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Overlay, OverlayContainer, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DevNotesPanel } from '../../ui/dev-notes/dev-notes-panel/dev-notes-panel';

/** localStorage key for the notes scratchpad (unchanged from when it lived in the command palette). */
export const DEV_NOTES_STORAGE_KEY = 'anessa-dev-notes';
/** CDK overlays sit at z-index 1000; one above, for browsers where overlays aren't top-layer popovers. */
export const DEV_NOTES_Z_INDEX = 1001;

/**
 * The internal-only notes scratchpad (`~notes` in the command palette, or
 * Ctrl+Alt+N): one persistent note, auto-saved to localStorage on this device.
 *
 * The panel is a plain CDK overlay rather than a MatDialog, on purpose:
 * - no backdrop, so the page — or another open dialog — stays clickable and
 *   selectable underneath while the notes are open;
 * - it doesn't register for the overlay keyboard dispatcher, so Escape still
 *   reaches a dialog the notes float over (MatDialog would swallow it as the
 *   topmost overlay). The panel handles its own Escape instead.
 *
 * Staying on top: CDK 21 shows overlays (dialogs included) as top-layer
 * popovers, where z-index doesn't apply and the most recently shown one wins.
 * So while the notes are open, any other overlay opening re-shows the notes,
 * moving them back to the top (see `raiseAboveLaterOverlays`).
 */
@Injectable({ providedIn: 'root' })
export class DevNotesService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly overlayContainer = inject(OverlayContainer);

  readonly text = signal<string>(this.load());
  private overlayRef: OverlayRef | null = null;
  /** Focus to give back on close — e.g. the input of the dialog underneath. */
  private previouslyFocused: HTMLElement | null = null;
  private stopRaising: (() => void) | null = null;

  isOpen(): boolean {
    return this.overlayRef !== null;
  }

  toggle(): void {
    if (this.isOpen()) this.close();
    else this.open();
  }

  open(): void {
    if (this.overlayRef) return;
    const active = this.document.activeElement;
    this.previouslyFocused = active instanceof HTMLElement ? active : null;
    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      panelClass: 'dev-notes-pane',
      positionStrategy: this.overlay.position().global().top('80px').right('24px'),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
    // Without popover support, every CDK overlay shares one z-index and stacks
    // by attach order, so a dialog opened after the notes would cover them.
    // One step above keeps the notes on top there.
    this.overlayRef.hostElement.style.zIndex = String(DEV_NOTES_Z_INDEX);
    this.overlayRef.attach(new ComponentPortal(DevNotesPanel, null, this.injector));
    this.stopRaising = this.raiseAboveLaterOverlays(this.overlayRef.hostElement);
  }

  /**
   * Re-shows the notes popover whenever another overlay's popover opens, so
   * the notes return to the top of the top layer. `toggle` doesn't bubble,
   * hence the capture-phase listener on the overlay container that holds
   * every overlay host. Returns the cleanup.
   */
  private raiseAboveLaterOverlays(notesHost: HTMLElement): () => void {
    const container = this.overlayContainer.getContainerElement();
    const onToggle = (event: Event): void => {
      if (event.target === notesHost) return;
      if ((event as Event & { newState?: string }).newState !== 'open') return;
      if (!notesHost.hasAttribute('popover')) return;
      try {
        notesHost.hidePopover();
        notesHost.showPopover();
      } catch {
        // Already hidden/removed mid-close — nothing to raise.
      }
    };
    container.addEventListener('toggle', onToggle, true);
    return () => container.removeEventListener('toggle', onToggle, true);
  }

  close(): void {
    if (!this.overlayRef) return;
    this.stopRaising?.();
    this.stopRaising = null;
    this.overlayRef.dispose();
    this.overlayRef = null;
    this.previouslyFocused?.focus();
    this.previouslyFocused = null;
  }

  /** Auto-saves on every keystroke — a scratchpad shouldn't need an explicit save step. */
  setText(text: string): void {
    this.text.set(text);
    try {
      localStorage.setItem(DEV_NOTES_STORAGE_KEY, text);
    } catch {
      // Best-effort — private browsing / storage quota shouldn't break typing.
    }
  }

  private load(): string {
    try {
      return localStorage.getItem(DEV_NOTES_STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  }
}
